import { Badge } from '@/components/ui/atoms/badge'
import { Button } from '@/components/ui/atoms/button'
import StudentAuthenticationDialog from '@/components/ui/organisms/student-authentication-dialog'
import { TOKEN_MARKUP } from '@/consts'
import { cn, getPrice, open } from '@/lib/client'
import { store as authentication_store } from '@/store/authentication'
import type { IAccount, IAuthentication } from '@/types/account'
import type { ICombo, ICourse, IModule, IProductChild } from '@/types/product'
import { useStore } from '@nanostores/react'
import {
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  FileText,
  ShoppingBag,
} from 'lucide-react'
import { atom } from 'nanostores'
import type React from 'react'
import { Fragment, useEffect, useState } from 'react'

const seeMoreAtom = atom<number | null>(null)

export default function ProductRelatedMaterial({
  product,
  total,
  to,
  handleClick,
}: {
  product: ICourse | ICombo | IModule
  total: number
  to: number
  handleClick: (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}) {
  const $seeMore = useStore(seeMoreAtom)
  const $authentication = useStore(authentication_store.authentication)

  const [user, setUser] = useState<(IAccount & IAuthentication) | null>(null)

  useEffect(() => {
    setUser($authentication)
  }, [$authentication])

  const getContentUrl = (
    child: IProductChild,
    product: ICourse | ICombo | IModule,
    token: string,
  ) =>
    child.children?.length
      ? `${product.content.link}&module=${child._id}&lesson=${child.children[0]._id}&chapter=${child.children[0].child}&auth=${token}&type=text`
      : ''

  const onClick = (
    child: IProductChild,
    product: ICourse | ICombo | IModule,
    token: string,
  ) => {
    const url = getContentUrl(child, product, token)
    if (url) open(url)
  }

  const renderContentButtons = (
    child: IProductChild,
    product: ICourse | ICombo | IModule,
  ) =>
    product.type === 'module' ? (
      <></>
    ) : product.type === 'combo' ? (
      <>
        <Button
          size={'sm'}
          variant={'ghost'}
          className="bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-600/10 hover:bg-sky-100 hover:text-sky-700 rounded-sm w-full"
        >
          <ArrowUpRight size={18} className="mr-2" /> Ver mais
        </Button>
      </>
    ) : user?._id ? (
      <>
        <Button
          size={'sm'}
          variant={'ghost'}
          className="rounded-sm w-full bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-600/10 hover:bg-sky-100 hover:text-sky-700"
          onClick={() => onClick(child, product, user.token)}
        >
          <FileText size={18} className="mr-2" /> Conteúdo demonstrativo
        </Button>
      </>
    ) : (
      <>
        <StudentAuthenticationDialog
          title="Olá, estudante"
          description="Para acessar o conteúdo, por favor entre com o seu acesso ou cadastre-se"
          source={getContentUrl(child, product, TOKEN_MARKUP)}
        >
          <Button
            size={'sm'}
            variant={'ghost'}
            className="rounded-sm w-full bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-600/10 hover:bg-sky-100 hover:text-sky-700"
          >
            <FileText size={18} className="mr-2" /> Conteúdo demonstrativo
          </Button>
        </StudentAuthenticationDialog>
      </>
    )

  const renderTotalComparision = () => (
    <>
      {total > 0 ? (
        <span className="text-sm leading-3 text-zinc-500">
          De <span className="line-through">{getPrice(total)}</span>
        </span>
      ) : (
        <></>
      )}
      <span className="text-md leading-3">
        {total > 0 ? 'Por' : ''} <strong>{getPrice(to)}</strong>
      </span>
    </>
  )

  const { type }: { type: string } = product.children.find((c) => c.name) || {
    type: 'Módulos',
  }
  const { type: subtype }: { type: string } = (product.children?.length &&
    'children' in product.children[0] &&
    product.children[0]?.children.find((c: { name: string }) => c.name)) || {
    type: 'Aulas',
  }

  return (
    <table className="table-auto not-prose w-full border">
      <thead className="bg-zinc-50 border-b">
        <tr>
          <th className="p-4 text-left">{type}</th>
          <th className={cn('p-4 hidden xl:table-cell')} />
          <th className={cn('p-4 hidden xl:table-cell')} />
        </tr>
      </thead>
      <tbody>
        {product.children.map((child_l1: IProductChild, key: number) => (
          <Fragment key={child_l1.name}>
            <tr
              className={cn('border-b bg-white', key % 2 !== 0 && 'bg-zinc-50')}
            >
              <td className="p-4 space-y-4">
                {product.type === 'module' ? (
                  <h3 className="text-sm font-semibold underline lg:no-underline">
                    {child_l1.name}
                  </h3>
                ) : (
                  <a
                    href={`/${product.type === 'combo' ? 'cursos' : 'modulos'}/${child_l1.permanentLink}`}
                  >
                    <div className="flex gap-2 items-center">
                      {product.type === 'combo' && (
                        <img
                          loading="lazy"
                          src={child_l1.icon || '/images/logo-color-thin.svg'}
                          className="object-contain rounded-lg p-2 bg-zinc-100 h-16 w-16"
                          width={64}
                          height={64}
                          alt={`Curso - ${product.name}`}
                        />
                      )}
                      <div>
                        {child_l1.percentage_off > 0 ? (
                          <Badge className="bg-emerald-500" variant="default">
                            {Math.round(child_l1.percentage_off)}% OFF
                          </Badge>
                        ) : (
                          <></>
                        )}
                        <h3 className="text-sm font-semibold underline lg:no-underline">
                          {child_l1.name}
                        </h3>
                      </div>
                    </div>
                  </a>
                )}

                <div
                  className={cn(
                    'space-y-2 lg:space-y-0 lg:flex lg:gap-3 xl:hidden',
                  )}
                >
                  {child_l1?.children?.length > 0 &&
                    child_l1?.children?.length > 0 &&
                    renderContentButtons(child_l1, product)}
                </div>
                <Button
                  variant={'link'}
                  className={cn(
                    'm-0 p-0',
                    product.type === 'combo' && 'hidden',
                  )}
                  onClick={(ev) => {
                    ev.preventDefault()
                    if ($seeMore === key) {
                      seeMoreAtom.set(null)
                    } else {
                      seeMoreAtom.set(key)
                    }
                  }}
                >
                  {!('children' in child_l1) ? (
                    <></>
                  ) : child_l1?.children.length > 0 && $seeMore === key ? (
                    <>
                      Ocultar {subtype.toLowerCase()}
                      <ChevronUp
                        className="relative top-[1px] ml-1 h-3 w-3 transition duration-200 group-data-[state=open]:rotate-180"
                        aria-hidden="true"
                      />
                    </>
                  ) : child_l1?.children.length > 0 ? (
                    <>
                      Exibir {subtype.toLowerCase()}
                      <ChevronDown
                        className="relative top-[1px] ml-1 h-3 w-3 transition duration-200 group-data-[state=open]:rotate-180"
                        aria-hidden="true"
                      />
                    </>
                  ) : (
                    <></>
                  )}
                </Button>
              </td>
              <td className={cn('p-4 w-60 hidden xl:table-cell')} />
              <td className={cn('p-4 w-60 hidden xl:table-cell')}>
                <div className="space-y-2 text-right">
                  {child_l1?.children?.length > 0 &&
                    renderContentButtons(child_l1, product)}
                </div>
              </td>
            </tr>
            {'children' in child_l1 &&
            child_l1.children &&
            child_l1.children.length &&
            $seeMore === key ? (
              <tr
                className={cn(
                  'border-b bg-white',
                  key % 2 !== 0 && 'bg-zinc-50',
                )}
              >
                <td className="py-4 px-8 text-sm" colSpan={3}>
                  <strong>O que você aprenderá neste módulo</strong>
                  <div
                    className={cn(
                      'grid lg:grid-rows-4 lg:grid-flow-col gap-4 mt-5',
                      Math.ceil(child_l1.children.length / 2) === 2 &&
                        'lg:grid-rows-2',
                      Math.ceil(child_l1.children.length / 2) === 3 &&
                        'lg:grid-rows-3',
                      Math.ceil(child_l1.children.length / 2) === 5 &&
                        'lg:grid-rows-5',
                      Math.ceil(child_l1.children.length / 2) === 6 &&
                        'lg:grid-rows-6',
                      Math.ceil(child_l1.children.length / 2) === 7 &&
                        'lg:grid-rows-7',
                      Math.ceil(child_l1.children.length / 2) === 8 &&
                        'lg:grid-rows-8',
                      Math.ceil(child_l1.children.length / 2) === 9 &&
                        'lg:grid-rows-9',
                      Math.ceil(child_l1.children.length / 2) === 10 &&
                        'lg:grid-rows-10',
                      Math.ceil(child_l1.children.length / 2) === 11 &&
                        'lg:grid-rows-11',
                      Math.ceil(child_l1.children.length / 2) === 12 &&
                        'lg:grid-rows-12',
                      Math.ceil(child_l1.children.length / 2) === 13 &&
                        'lg:grid-rows-13',
                      Math.ceil(child_l1.children.length / 2) === 14 &&
                        'lg:grid-rows-14',
                      Math.ceil(child_l1.children.length / 2) === 15 &&
                        'lg:grid-rows-15',
                      Math.ceil(child_l1.children.length / 2) === 16 &&
                        'lg:grid-rows-16',
                    )}
                  >
                    {child_l1.children.map(
                      (child_l2: {
                        order: number
                        name: string
                        _id: string
                      }) => (
                        <div key={child_l2._id}>
                          {child_l2.order < 9
                            ? `0${child_l2.order + 1}`
                            : `${child_l2.order + 1}`}{' '}
                          - {child_l2.name}
                        </div>
                      ),
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              <></>
            )}
          </Fragment>
        ))}
      </tbody>
      <tfoot className="bg-zinc-50">
        <tr>
          <td className="p-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-0">
                <strong>Total</strong>
                {total > 0 ? (
                  <strong className={cn('text-sm text-primary')}>
                    Você economizará {getPrice(total - to)}
                  </strong>
                ) : (
                  <></>
                )}
              </div>

              <div
                className={cn(
                  'flex flex-col text-2xl gap-2 xl:text-right xl:hidden',
                )}
              >
                {renderTotalComparision()}
              </div>

              <Button
                size={'sm'}
                className={cn('rounded-sm w-full mt-2 xl:hidden')}
                onClick={handleClick}
              >
                <ShoppingBag size={18} className="mr-2" /> Comprar
              </Button>
            </div>
          </td>
          <td className={cn('p-4 hidden xl:table-cell')}>
            <div className="flex flex-col gap-2 text-right">
              {renderTotalComparision()}
            </div>
          </td>
          <td className={cn('p-4 hidden xl:table-cell')}>
            <Button
              size={'sm'}
              className={cn('rounded-sm w-full')}
              onClick={handleClick}
            >
              <ShoppingBag size={18} className="mr-2" /> Comprar
            </Button>
          </td>
        </tr>
      </tfoot>
    </table>
  )
}
